import { isBooleanTrue, isListEmpty, isListNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "./objectUtils";
import { calculateAddOnQuantity } from "./addOnUtils";
import PrescriptionAddOnDetails from "../models/PrescriptionAddOnDetails";

export const buildPrescriptionAddOnDetails = (addOn, prescribedAddOn, providerInfo = {}) => new PrescriptionAddOnDetails({
    productId: addOn?.productId ?? null,
    variantId: addOn?.variantId ?? null,
    name: prescribedAddOn?.name ?? addOn?.name ?? null,
    sku: addOn?.sku ?? null,
    medRefId: prescribedAddOn?.medId ?? addOn?.medRefId ?? null,
    eRXId: prescribedAddOn?.rxId ?? null,
    instructions: prescribedAddOn?.directions ?? null,
    quantity: !isNaN(prescribedAddOn?.quantity) ? Number(prescribedAddOn.quantity) : (addOn?.quantity ?? 1),
    refills: !isNaN(prescribedAddOn?.refills) ? Number(prescribedAddOn.refills) : 0,
    providerFirstName: providerInfo?.providerFirstName ?? null,
    providerLastName: providerInfo?.providerLastName ?? null,
    providerNPI: providerInfo?.providerNPI ?? null,
    writtenAt: providerInfo?.writtenAt ?? null,
    expirationDate: providerInfo?.expirationDate ?? null
}).toDto();

export const resolvePrescriptionRequiredAddOnsForPreferences = (prescriptionRequests = [], supplierId, products = [], variants = []) => {
    if (isListEmpty(prescriptionRequests) || isStringEmpty(supplierId)) return [];

    const requiredAddOns = [];

    for (const request of prescriptionRequests) {
        if (isStringEmpty(request?.productId) || isStringEmpty(request?.variantId)) continue;

        const product = products.find((item) => item.id === request.productId);
        const variant = variants.find((item) => item.productId === request.productId && item.id === request.variantId);
        // console.log('product', product);
        // console.log('variant', variant);
        const variantSupplierMapping = isListNotEmpty(variant?.supplierMappings)
            ? variant.supplierMappings.find((mapping) => mapping.supplierId === supplierId)
            : null;
        if (isObjectEmpty(product) || isObjectEmpty(variantSupplierMapping)) continue;

        // console.log('variantSupplierMapping', variantSupplierMapping);
        const productRuleCandidates = isListNotEmpty(product.addOnRules)
            ? product.addOnRules.filter((rule) => (
                rule.enabled !== false &&
                isBooleanTrue(rule.requiresPrescription) &&
                isStringNotEmpty(rule.productId) &&
                isStringNotEmpty(rule.variantId)
            )).map((rule) => ({ ...rule, source: 'PRODUCT_RULE', priority: 2 }))
            : [];
        const supplierOverrideCandidates = isListNotEmpty(variantSupplierMapping.addOns)
            ? variantSupplierMapping.addOns.filter((addOn) => (
                isStringNotEmpty(addOn.productId) &&
                isStringNotEmpty(addOn.variantId)
            )).map((addOn) => ({ ...addOn, source: 'SUPPLIER_MAPPING', priority: 1 }))
            : [];
        const groupedCandidates = {};
        for (const candidate of [...productRuleCandidates, ...supplierOverrideCandidates]) {
            const key = `${candidate.productId}::${candidate.variantId}`;
            if (!groupedCandidates[key]) groupedCandidates[key] = [];
            groupedCandidates[key].push(candidate);
        }

        for (const candidates of Object.values(groupedCandidates)) {
            candidates.sort((a, b) => a.priority - b.priority);
            const hasSupplierOverride = candidates.some((candidate) => candidate.source === 'SUPPLIER_MAPPING');
            const candidate = hasSupplierOverride ? candidates.find((item) => item.source === 'SUPPLIER_MAPPING') : candidates[0];
            if (!isBooleanTrue(candidate?.requiresPrescription)) continue;
            const addOnProduct = products.find((item) => item.id === candidate.productId && item.productType === 'ADD_ON');
            const addOnVariant = variants.find((item) => item.productId === candidate.productId && item.id === candidate.variantId);
            const addOnSupplierMapping = isListNotEmpty(addOnVariant?.supplierMappings)
                ? addOnVariant.supplierMappings.find((mapping) => mapping.supplierId === supplierId)
                : null;
            if (!isObjectNotEmpty(addOnProduct) || !isObjectNotEmpty(addOnVariant) || !isStringNotEmpty(addOnSupplierMapping?.refId)) continue;

            requiredAddOns.push({
                productId: candidate.productId,
                variantId: candidate.variantId,
                name: candidate.name || addOnVariant.name || addOnProduct.name || null,
                sku: candidate.sku || addOnVariant.sku || addOnProduct.sku || null,
                medRefId: addOnSupplierMapping.refId,
                quantity: calculateAddOnQuantity(candidate.quantityMode || 'FIXED', candidate.quantity || 1, addOnSupplierMapping?.quantity || 1),
                requiresPrescription: true,
                parentPrescriptionRequest: request
            });
        }
    }

    return requiredAddOns;
};

export const aggregateBelugaAddOnPreferences = (requiredAddOns = [], supplierId, addOnVariants = [], testMode = false, testModeMedId = null) => {
    const aggregateMap = {};

    for (const addOn of requiredAddOns) {
        const addOnVariant = addOnVariants.find((variant) => variant.productId === addOn.productId && variant.id === addOn.variantId);
        const addOnSupplierMapping = isListNotEmpty(addOnVariant?.supplierMappings)
            ? addOnVariant.supplierMappings.find((mapping) => mapping.supplierId === supplierId)
            : null;
        const medId = testMode === true && isStringNotEmpty(testModeMedId) ? testModeMedId : (addOn.medRefId || addOnSupplierMapping?.refId || null);
        if (!isStringNotEmpty(medId)) continue;

        const parentRequest = addOn.parentPrescriptionRequest;
        const parentFillCount = (!isNaN(parentRequest?.refills) ? Number(parentRequest.refills) : 0) + 1;
        if (!aggregateMap[medId]) {
            aggregateMap[medId] = {
                name: addOn.name || addOnVariant?.name || 'Add-On',
                strength: addOnVariant?.strength ?? 'NA',
                quantity: String(addOn.quantity || addOnSupplierMapping?.quantity || 1),
                refills: 0,
                medId: medId
            };
        } else {
            const existingQuantity = !isNaN(aggregateMap[medId].quantity) ? Number(aggregateMap[medId].quantity) : 1;
            const nextQuantity = !isNaN(addOn.quantity) ? Number(addOn.quantity) : existingQuantity;
            aggregateMap[medId].quantity = String(Math.max(existingQuantity, nextQuantity));
        }
        aggregateMap[medId].refills = aggregateMap[medId].refills + Math.max(parentFillCount, 1);
    }

    return Object.values(aggregateMap).map((preference) => ({
        ...preference,
        refills: String(Math.max(preference.refills - 1, 0))
    }));
};

export const buildBelugaPatientPreferences = (encounter, supplierId, products = [], variants = [], testMode = false, testModeMedId = null) => {
    const prescriptionRequests = isListNotEmpty(encounter?.prescriptionRequests) ? encounter.prescriptionRequests : [];
    const medicationPreferences = prescriptionRequests.map((prescriptionRequest) => ({
        name: prescriptionRequest.name,
        strength: prescriptionRequest?.strength ?? 'NA',
        quantity: String(prescriptionRequest?.quantity ?? 1),
        refills: String(prescriptionRequest?.refills ?? 0),
        medId: testMode === true && isStringNotEmpty(testModeMedId) ? testModeMedId : prescriptionRequest.medRefId
    }));
    // console.log('prescriptionRequests', prescriptionRequests);
    // console.log('supplierId', supplierId);
    const requiredAddOns = resolvePrescriptionRequiredAddOnsForPreferences(prescriptionRequests, supplierId, products, variants);
    // console.log('requiredAddOns', requiredAddOns);
    const addOnProductIds = new Set(products.filter((product) => product.productType === 'ADD_ON').map((product) => product.id));
    const addOnVariants = variants.filter((variant) => addOnProductIds.has(variant.productId));
    const addOnPreferences = aggregateBelugaAddOnPreferences(requiredAddOns, supplierId, addOnVariants, testMode, testModeMedId);
    // console.log('addOnPreferences', addOnPreferences);
    return [...medicationPreferences, ...addOnPreferences].filter((preference) => isObjectNotEmpty(preference) && isStringNotEmpty(preference.medId));
};

export const isAddOnMedId = (medId, products = [], variants = []) => {
    if (!isStringNotEmpty(medId)) return false;
    const addOnProductIds = new Set(products.filter((product) => product.productType === 'ADD_ON').map((product) => product.id));
    return variants.some((variant) => addOnProductIds.has(variant.productId) && isListNotEmpty(variant.supplierMappings) && variant.supplierMappings.some((mapping) => mapping.refId === medId));
};
